import {useEffect, useMemo, useState} from 'react';

const STORAGE_KEY = 'itcams-theme';
const THEME_CHANGE_EVENT = 'itcams-theme-change';

function normalizeTheme(theme: string | null | undefined): string {
    return theme === 'dark' ? 'dark' : 'light';
}

function getInitialTheme(): string {
    if (typeof window === 'undefined') {
        return 'light';
    }

    const savedTheme = window.localStorage.getItem(STORAGE_KEY);
    if (savedTheme) {
        return normalizeTheme(savedTheme);
    }

    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }

    return 'light';
}

export function useTheme(): [string, (theme: string) => void] {
    const [theme, setThemeState] = useState<string>(getInitialTheme);

    useEffect(() => {
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(theme);
        window.localStorage.setItem(STORAGE_KEY, theme);

        // Dopasuj favSVG do aktywnego motywu: ciemny motyw -> jasny (biały) znak,
        // jasny motyw -> ciemny znak. Dzięki temu ikona jest czytelna na obu tłach.
        const iconLink = document.querySelector<HTMLLinkElement>(
            'link[rel="icon"][type="image/svg+xml"]',
        );
        if (iconLink) {
            const base = process.env.PUBLIC_URL ?? '';
            iconLink.href = `${base}/${theme === 'dark' ? 'favicon-dark.svg' : 'favicon.svg'}`;
        }

        window.dispatchEvent(
            new CustomEvent<string>(THEME_CHANGE_EVENT, {
                detail: theme,
            }),
        );
    }, [theme]);

    useEffect(() => {
        const syncTheme = (nextTheme: string) => {
            const normalizedTheme = normalizeTheme(nextTheme);
            setThemeState((currentTheme) =>
                currentTheme === normalizedTheme ? currentTheme : normalizedTheme,
            );
        };

        const handleThemeChange = (event: Event) => {
            syncTheme((event as CustomEvent<string>).detail);
        };

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_KEY || !event.newValue) {
                return;
            }

            syncTheme(event.newValue);
        };

        window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);

    const setTheme = useMemo(
        () => (nextTheme: string) => {
            setThemeState(normalizeTheme(nextTheme));
        },
        [],
    );

    return [theme, setTheme];
}
